/**
 * BookingShareSheet — «Mi link y QR de reservas» (Bloque 3). Mismo destino para todo: /r/:usuario.
 * Compartir (copiar · WhatsApp · Instagram · correo · mensajes · sistema), QR en pantalla completa y PNG
 * de alta resolución para imprimir. Cada vía marca su origen (link / qr_screen / qr_print) para Estadísticas.
 */
import { useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { Copy, Share2, MessageCircle, Instagram, Mail, MessageSquare, Maximize2, Download, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { bookingLink } from '@/lib/bookingOrigin';
import { svgToPngBlob, downloadBlob } from '@/lib/qrPng';
import { BookingQrFullscreen } from './BookingQrFullscreen';
import { tr } from '@/contexts/LanguageContext';

const ActionBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button type="button" onClick={onClick} className="flex flex-col items-center gap-1.5 w-[60px] shrink-0" data-testid={testid}>
    <span className="w-12 h-12 rounded-full bg-secondary border border-border flex items-center justify-center text-foreground hover:border-primary/60 transition-colors"><Icon className="w-5 h-5" /></span>
    <span className="text-[11px] text-muted-foreground truncate max-w-full">{label}</span>
  </button>
);

export const BookingShareSheet = ({ open, onOpenChange, username, name, avatar, kind = 'barber' }) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef(null);
  const link = bookingLink(username, 'link');
  const screenUrl = bookingLink(username, 'qr_screen');
  const printUrl = bookingLink(username, 'qr_print');
  const label = kind === 'shop' ? tr("Reserva tu cita en {0}", name) : tr("Reserva tu cita con {0}", name);
  const text = `${label} 👉 ${link}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success(tr("Enlace copiado.")); return; }
    catch { /* sin permiso de portapapeles: respaldo con selección */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = link; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); const ok = document.execCommand('copy'); document.body.removeChild(ta);
      if (ok) { toast.success(tr("Enlace copiado.")); return; }
    } catch { /* sigue al error */ }
    toast.error(tr("No pudimos copiar el enlace."));
  };
  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  const instagram = async () => {
    await copy();
    toast(tr("Pega el enlace en tu bio o en una historia de Instagram."));
    window.open('https://www.instagram.com/', '_blank', 'noopener');
  };
  const email = () => { window.location.href = `mailto:?subject=${encodeURIComponent(label)}&body=${encodeURIComponent(text)}`; };
  const sms = () => {
    const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent || '');
    window.location.href = `sms:${ios ? '&' : '?'}body=${encodeURIComponent(text)}`;
  };
  const native = async () => {
    if (!navigator.share) { copy(); return; }
    try { await navigator.share({ title: label, text: label, url: link }); }
    catch (e) { if (e?.name !== 'AbortError') toast.error(tr("No pudimos compartir.")); }
  };
  const download = async () => {
    const svg = printRef.current?.querySelector('svg');
    if (!svg) return;
    setDownloading(true);
    try {
      const blob = await svgToPngBlob(svg, { title: name, subtitle: tr("Escanea para reservar · Ziyup"), size: 1024 });
      downloadBlob(blob, `qr-reservas-${username}.png`);
      toast.success(tr("QR descargado en alta calidad (PNG)."));
    } catch { toast.error(tr("No pudimos generar el PNG.")); }
    finally { setDownloading(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border w-[calc(100vw-1.5rem)] max-w-md rounded-2xl p-0 overflow-x-hidden max-h-[92vh] overflow-y-auto [&>*]:min-w-0 [&>*]:max-w-full" data-testid="booking-share-sheet">
          <DialogHeader className="px-4 pt-4 pb-2 border-b border-border">
            <DialogTitle className="text-base font-display font-bold flex items-center gap-2"><QrCode className="w-4 h-4 text-primary" /> {tr("Mi link y QR de reservas")}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">{kind === 'shop' ? tr("Quien lo abra elige barbero y reserva directo, sin perfil ni inicio de sesión.") : tr("Quien lo abra reserva contigo directo, sin pasar por tu perfil ni iniciar sesión.")}</DialogDescription>
          </DialogHeader>
          <div className="px-4 py-4 space-y-4">
            <div className="flex items-center gap-2 bg-background border border-border rounded-xl px-3 py-2.5 min-w-0" data-testid="booking-share-link-field">
              <p className="text-xs text-foreground font-mono truncate flex-1 min-w-0 overflow-hidden" title={link} data-testid="booking-share-url">{link}</p>
              <Button size="sm" variant="outline" onClick={copy} className="h-8 rounded-full gap-1 shrink-0" data-testid="booking-share-copy"><Copy className="w-3.5 h-3.5" /> {tr("Copiar")}</Button>
            </div>
            <div className="flex flex-wrap items-start justify-center gap-x-2 gap-y-3" data-testid="booking-share-actions">
              <ActionBtn icon={MessageCircle} label="WhatsApp" onClick={whatsapp} testid="booking-share-whatsapp" />
              <ActionBtn icon={Instagram} label="Instagram" onClick={instagram} testid="booking-share-instagram" />
              <ActionBtn icon={Mail} label={tr("Correo")} onClick={email} testid="booking-share-email" />
              <ActionBtn icon={MessageSquare} label={tr("Mensajes")} onClick={sms} testid="booking-share-sms" />
              <ActionBtn icon={Share2} label={tr("Más")} onClick={native} testid="booking-share-native" />
            </div>
            <div className="bg-white rounded-2xl p-4 flex flex-col items-center gap-3" data-testid="booking-share-qr">
              <QRCode value={screenUrl} size={180} level="M" />
              <p className="text-[11px] text-black/60">{tr("Muéstralo en tu pantalla o imprímelo en tu local.")}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => setFullscreen(true)} className="h-10 rounded-full gap-1.5 text-xs" data-testid="booking-share-qr-fullscreen"><Maximize2 className="w-4 h-4" /> {tr("Pantalla completa")}</Button>
              <Button onClick={download} disabled={downloading} className="h-10 rounded-full gap-1.5 text-xs bg-primary text-primary-foreground" data-testid="booking-share-qr-download"><Download className="w-4 h-4" /> {tr("Descargar PNG")}</Button>
            </div>
            <div ref={printRef} className="absolute -left-[9999px] top-0" aria-hidden><QRCode value={printUrl} size={512} level="H" /></div>
          </div>
        </DialogContent>
      </Dialog>
      <BookingQrFullscreen open={fullscreen} onClose={() => setFullscreen(false)} url={screenUrl} name={name} avatar={avatar} />
    </>
  );
};

export default BookingShareSheet;
